import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";

import { accountDisplayName, accountIdentityAside } from "~/neucleos/accountIdentity";
import { selectProfileWorldKey, useActorProfile } from "~/neucleos/actorProfileStore";
import { COCKPIT_ORIGIN } from "~/neucleos/cockpitOrigin";
import {
  readStatusRefreshingWorlds,
  signOutOfNeucleos,
  startNeucleosSignIn,
} from "~/neucleos/loginCeremony";
import {
  pendingRevocationNotice,
  pendingRevocationRecheckDelayMs,
  refreshNeucleosWorlds,
  signInEndedNotice,
  signInEndingNotice,
  useNeucleosWorldStore,
  worldsNotHeldNotice,
} from "~/neucleos/worldStore";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { SettingsRow } from "./settingsLayout";
import { isWebHost } from "~/webHost";

/**
 * The session row of the Account page: who you are signed in as, the door
 * to manage that identity in the Cockpit, and the sign-in / sign-out
 * ceremony itself. Sign-in is the loopback popup handoff that deposits a
 * grant per entitled World on this Mac — a link to the Cockpit website does
 * not do that, which is why "Log in" was never a door. Sign-out revokes those
 * grants and clears custody here; any the World has not confirmed revoked
 * are named until it does (the Desk keeps retrying them).
 */

type Phase = "idle" | "signing-in" | "signing-out" | "settling";

/** A door out of the Desk to the Cockpit: a Button rendering an anchor to the one shared origin. */
function DoorButton({
  children,
  href,
  ...dataProps
}: {
  readonly children: ReactNode;
  readonly href: string;
} & Record<`data-${string}`, string>): ReactElement {
  return (
    <Button
      render={<a href={href} rel="noopener noreferrer" target="_blank" />}
      size="xs"
      variant="outline"
      {...dataProps}
    >
      {children}
    </Button>
  );
}

const START_FAILED =
  "Sign-in could not start. The login door answers only on this Mac's Desk, not through a remote session.";
const SIGN_OUT_FAILED =
  "Sign-out did not complete: the local Cleo server refused or was unreachable.";
// cleo-web (W-9f33f8b5): the browser has no local server to blame; its sign-out ends the page's own session.
const WEB_HOST_SIGN_OUT_FAILED = "Sign-out did not complete. Reload the page and try again.";

/**
 * Any other way to sign out of Neucleos (the desktop menu's action) asks here, in the same words: it opens Account
 * and this confirmation, never signing out by itself (#1450 review).
 */
const signOutRequests = new Set<() => void>();
let signOutRequested = false;
export function requestNeucleosSignOut(): void {
  if (signOutRequests.size === 0) {
    signOutRequested = true;
    return;
  }
  for (const open of signOutRequests) open();
}

export function AccountSessionRow(): ReactElement {
  const identity = useNeucleosWorldStore((state) => state.identity);
  const loaded = useNeucleosWorldStore((state) => state.loaded);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const pendingRevocations = useNeucleosWorldStore((state) => state.pendingRevocations);
  const signInEndsAt = useNeucleosWorldStore((state) => state.signInEndsAt);
  const worldsNotHeld = useNeucleosWorldStore((state) => state.worldsNotHeld);
  // US-CAN-08: the row is titled by the person's name; the sign-in email
  // stays visible as the aside (it is the identity precondition, not the face).
  const profileWorldKey = useNeucleosWorldStore(selectProfileWorldKey);
  const profile = useActorProfile(profileWorldKey);
  const accountName = accountDisplayName(profile, identity);
  const accountAside = accountIdentityAside(accountName, identity);
  const [phase, setPhase] = useState<Phase>("idle");
  // 7 Oct 2026: a plain "Sign out" read as signing out of Google; it signed him out of every World. It now says
  // what it signs out of, and asks first.
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  useEffect(() => {
    const open = () => setConfirmingSignOut(true);
    signOutRequests.add(open);
    if (signOutRequested) {
      signOutRequested = false;
      open();
    }
    return () => {
      signOutRequests.delete(open);
    };
  }, []);
  const [notice, setNotice] = useState<string | null>(null);
  const stopWatch = useRef<(() => void) | null>(null);

  useEffect(() => () => stopWatch.current?.(), []);

  const signIn = async () => {
    // Stop an older watch first: its onDone would otherwise land after the
    // new phase and read as "idle" while the new ceremony is still running.
    stopWatch.current?.();
    stopWatch.current = null;
    setNotice(null);
    if (isWebHost()) {
      // cleo-web: the page signs in through Clerk's own form on the landing (WebHostLanding.tsx); the Desk's loopback
      // login door does not exist here.
      window.location.assign("/");
      return;
    }
    setPhase("signing-in");
    try {
      const stop = await startNeucleosSignIn({
        read: readStatusRefreshingWorlds,
        onDone: () => setPhase("idle"),
      });
      if (stop === null) {
        setNotice(START_FAILED);
        setPhase("idle");
        return;
      }
      stopWatch.current = stop;
      setPhase("settling");
    } catch {
      setNotice(START_FAILED);
      setPhase("idle");
    }
  };

  const signOut = async () => {
    stopWatch.current?.();
    stopWatch.current = null;
    setNotice(null);
    setPhase("signing-out");
    const outcome = await signOutOfNeucleos();
    setPhase("idle");
    if (!outcome.ok) setNotice(isWebHost() ? WEB_HOST_SIGN_OUT_FAILED : SIGN_OUT_FAILED);
    else if (outcome.notice) setNotice(`Signed out on this Mac. ${outcome.notice}`);
    else if (outcome.unrevoked > 0) {
      setNotice(
        `Signed out on this Mac. ${
          pendingRevocationNotice(outcome.pending) ??
          `${outcome.unrevoked} World grant${outcome.unrevoked === 1 ? "" : "s"} could not be revoked yet; this Mac keeps trying.`
        }`,
      );
    }
  };

  // W-2a2288b5: while any World access outlives the sign-out, say so, even
  // after the page is reopened; the Desk is still revoking it. The row reads
  // the status door again just after each retry the Desk makes, so it stops
  // saying so once the World confirms.
  const pendingNotice = signedIn ? null : pendingRevocationNotice(pendingRevocations);
  useEffect(() => {
    const delay = pendingRevocationRecheckDelayMs(pendingRevocations, Date.now());
    if (delay === null) return;
    const timer = setTimeout(() => void refreshNeucleosWorlds(), delay);
    return () => clearTimeout(timer);
  }, [pendingRevocations]);

  // W-2308dc50: the Desk renews the sign-in while in use, up to its fixed
  // end; a few days before it, say so and offer the sign-in beside it.
  const endingNotice = signedIn ? signInEndingNotice(signInEndsAt) : null;
  // W-610f1054: a World joined since this sign-in that the Desk has not brought yet (it asks the World itself at once;
  // this stays while the World will not grant it to this sign-in): Sign in again brings every World the person has.
  const joinedNotice = signedIn ? worldsNotHeldNotice(worldsNotHeld) : null;
  // W-4a6095af: a sign-in that ended says so; a Desk never signed in (or signed out) invites.
  const endedNotice = useNeucleosWorldStore(signInEndedNotice);

  const description =
    notice ??
    pendingNotice ??
    endingNotice ??
    (phase === "settling"
      ? "Finish signing in in your browser. This page updates the moment you are signed in."
      : signedIn
        ? (joinedNotice ??
          `${accountAside === null ? "" : `Signed in as ${accountAside}. `}Your Neucleos identity follows you across devices and Worlds.`)
        : (endedNotice ?? "Sign in to enter your Worlds."));

  return (
    <>
      <SettingsRow
        id="account-session-row"
        data-settings-session={signedIn ? "signed-in" : "signed-out"}
        data-settings-session-phase={phase}
        title={signedIn ? (accountName ?? "Signed in") : "Not signed in"}
        description={description}
        control={
          signedIn ? (
            <>
              {/* No "Refresh access": a signed-in person's tools are live (PR
                #66); signing in again is never how they get new ones. It is how
                a World joined since the sign-in comes here (W-610f1054). */}
              {endingNotice || joinedNotice ? (
                <Button
                  data-settings-sign-in-again=""
                  disabled={phase !== "idle"}
                  onClick={() => void signIn()}
                  size="xs"
                  type="button"
                >
                  {phase === "idle" ? "Sign in again" : "Signing in…"}
                </Button>
              ) : null}
              <DoorButton href={COCKPIT_ORIGIN} data-settings-manage-account="">
                Manage account
              </DoorButton>
              <Button
                data-settings-sign-out=""
                disabled={phase !== "idle"}
                onClick={() => setConfirmingSignOut(true)}
                size="xs"
                type="button"
                variant="outline"
              >
                {phase === "signing-out" ? "Signing out…" : "Sign out of Neucleos"}
              </Button>
            </>
          ) : (
            <Button
              data-settings-sign-in=""
              disabled={!loaded || phase !== "idle"}
              onClick={() => void signIn()}
              size="xs"
              type="button"
            >
              {phase === "idle" ? "Sign in" : "Signing in…"}
            </Button>
          )
        }
      />
      <AlertDialog
        open={confirmingSignOut && signedIn && phase === "idle"}
        onOpenChange={(open) => (open ? null : setConfirmingSignOut(false))}
      >
        <AlertDialogPopup data-settings-sign-out-confirm="">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isWebHost()
                ? "Sign out of Neucleos in this browser?"
                : "Sign out of Neucleos on this Mac?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll be signed out of all your Worlds here; your Google, Gmail and other
              connections are separate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingSignOut(false)}
              data-settings-sign-out-cancel=""
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setConfirmingSignOut(false);
                void signOut();
              }}
              data-settings-sign-out-apply=""
            >
              Sign out of Neucleos
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
